// store/slices/posPrinterSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  POSPrinterService,
} from "../../../services/POS/PrinterService";
import {
  IPrinter,
  ICreatePrinter,
  IUpdatePrinter,
  IPrinterFilters,
  IPrinterPagedResponse,
} from "../../../models/POS/Printer.models";

// Estado inicial
interface POSPrinterState {
  printers: IPrinter[];
  currentPrinter: IPrinter | null;
  defaultPrinter: IPrinter | null;
  printersByTerminal: Record<number, IPrinter[]>; // TerminalId -> Printers[]

  loading: boolean;
  loadingCurrent: boolean;
  loadingDefault: boolean;
  loadingTerminal: Record<number, boolean>;

  error: string | null;
  currentError: string | null;
  defaultError: string | null;
  terminalErrors: Record<number, string>;

  // Paginación
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };

  // Filtros actuales
  currentFilters: IPrinterFilters;

  // Cache timestamp
  lastUpdated: number | null;
}

const initialState: POSPrinterState = {
  printers: [],
  currentPrinter: null,
  defaultPrinter: null,
  printersByTerminal: {},

  loading: false,
  loadingCurrent: false,
  loadingDefault: false,
  loadingTerminal: {},

  error: null,
  currentError: null,
  defaultError: null,
  terminalErrors: {},

  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  },

  currentFilters: {},
  lastUpdated: null,
};

// Async Thunks
export const fetchPrinters = createAsyncThunk(
  "posPrinter/fetchPrinters",
  async (filters: IPrinterFilters = {}, { rejectWithValue }) => {
    try {
      const service = new POSPrinterService();
      return await service.getAll(filters);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Error al cargar impresoras"
      );
    }
  }
);

export const fetchPrintersPaged = createAsyncThunk(
  "posPrinter/fetchPrintersPaged",
  async (filters: IPrinterFilters, { rejectWithValue }) => {
    try {
      const service = new POSPrinterService();
      const response = await service.getPaged(filters);
      return {
        items: response.items,
        totalCount: response.totalCount,
        pageNumber: filters.pageNumber || 1,
        pageSize: 10, // O el valor que uses
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Error al cargar impresoras paginadas"
      );
    }
  }
);

export const fetchPrinterById = createAsyncThunk(
  "posPrinter/fetchPrinterById",
  async (printerId: string, { rejectWithValue }) => {
    try {
      const service = new POSPrinterService();
      return await service.getById(printerId);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Error al cargar la impresora"
      );
    }
  }
);

export const createPrinter = createAsyncThunk(
  "posPrinter/createPrinter",
  async (printerData: ICreatePrinter, { rejectWithValue }) => {
    try {
      const service = new POSPrinterService();
      return await service.create(printerData);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Error al crear la impresora"
      );
    }
  }
);

export const updatePrinter = createAsyncThunk(
  "posPrinter/updatePrinter",
  async (printerData: IUpdatePrinter, { rejectWithValue }) => {
    try {
      const service = new POSPrinterService();
      return await service.update(printerData);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Error al actualizar la impresora"
      );
    }
  }
);

export const deletePrinter = createAsyncThunk(
  "posPrinter/deletePrinter",
  async (printerId: string, { rejectWithValue }) => {
    try {
      const service = new POSPrinterService();
      await service.delete(printerId);
      return printerId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Error al eliminar la impresora"
      );
    }
  }
);

export const setDefaultPrinter = createAsyncThunk(
  "posPrinter/setDefaultPrinter",
  async (printerId: string, { rejectWithValue }) => {
    try {
      const service = new POSPrinterService();
      return await service.setDefault(printerId);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Error al establecer impresora como predeterminada"
      );
    }
  }
);

export const fetchDefaultPrinter = createAsyncThunk(
  "posPrinter/fetchDefaultPrinter",
  async (_, { rejectWithValue }) => {
    try {
      const service = new POSPrinterService();
      return await service.getDefault();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Error al cargar impresora predeterminada"
      );
    }
  }
);

export const fetchPrintersByTerminal = createAsyncThunk(
  "posPrinter/fetchPrintersByTerminal",
  async (terminalId: number, { rejectWithValue }) => {
    try {
      const service = new POSPrinterService();
      return {
        terminalId,
        printers: await service.getByTerminal(terminalId),
      };
    } catch (error: any) {
      return rejectWithValue({
        terminalId,
        error:
          error.response?.data?.message ||
          "Error al cargar impresoras del terminal",
      });
    }
  }
);

export const activatePrinter = createAsyncThunk(
  "posPrinter/activatePrinter",
  async (printerId: string, { rejectWithValue }) => {
    try {
      const service = new POSPrinterService();
      return await service.activate(printerId);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Error al activar la impresora"
      );
    }
  }
);

// Slice
const posPrinterSlice = createSlice({
  name: "posPrinter",
  initialState,
  reducers: {
    // Actions síncronas
    clearCurrentPrinter: state => {
      state.currentPrinter = null;
      state.currentError = null;
    },

    clearError: state => {
      state.error = null;
      state.currentError = null;
      state.defaultError = null;
    },

    setFilters: (state, action: PayloadAction<IPrinterFilters>) => {
      state.currentFilters = action.payload;
    },

    clearFilters: state => {
      state.currentFilters = {};
    },

    updatePrinterInList: (state, action: PayloadAction<IPrinter>) => {
      const index = state.printers.findIndex(
        p => p.poS_PrinterId === action.payload.poS_PrinterId
      );
      if (index !== -1) {
        state.printers[index] = action.payload;
      }

      // Actualizar también en printersByTerminal si existe
      const terminalId = action.payload.poS_TerminalId;
      if (state.printersByTerminal[terminalId]) {
        const terminalIndex = state.printersByTerminal[terminalId].findIndex(
          p => p.poS_PrinterId === action.payload.poS_PrinterId
        );
        if (terminalIndex !== -1) {
          state.printersByTerminal[terminalId][terminalIndex] = action.payload;
        }
      }

      // Si es la impresora actual, actualizarla
      if (
        state.currentPrinter?.poS_PrinterId === action.payload.poS_PrinterId
      ) {
        state.currentPrinter = action.payload;
      }

      // Si es la impresora default, actualizarla
      if (
        state.defaultPrinter?.poS_PrinterId === action.payload.poS_PrinterId
      ) {
        state.defaultPrinter = action.payload;
      }
    },

    removePrinterFromList: (state, action: PayloadAction<string>) => {
      const printerId = action.payload;
      state.printers = state.printers.filter(
        p => p.poS_PrinterId !== printerId
      );

      // Remover de todos los terminales
      Object.keys(state.printersByTerminal).forEach(key => {
        const terminalId = parseInt(key);
        state.printersByTerminal[terminalId] = state.printersByTerminal[
          terminalId
        ].filter(p => p.poS_PrinterId !== printerId);
      });

      // Si es la impresora actual, limpiarla
      if (state.currentPrinter?.poS_PrinterId === printerId) {
        state.currentPrinter = null;
      }

      // Si es la impresora default, limpiarla
      if (state.defaultPrinter?.poS_PrinterId === printerId) {
        state.defaultPrinter = null;
      }
    },

    clearCache: state => {
      state.printers = [];
      state.printersByTerminal = {};
      state.defaultPrinter = null;
      state.lastUpdated = null;
    },

    resetPrinterState: () => initialState,
  },
  extraReducers: builder => {
    // fetchPrinters
    builder.addCase(fetchPrinters.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPrinters.fulfilled, (state, action) => {
      state.loading = false;
      state.printers = action.payload;
      state.lastUpdated = Date.now();
    });
    builder.addCase(fetchPrinters.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // fetchPrintersPaged
    builder.addCase(fetchPrintersPaged.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPrintersPaged.fulfilled, (state, action) => {
      state.loading = false;
      state.printers = action.payload.items;
      state.pagination = {
        currentPage: action.payload.pageNumber,
        pageSize: action.payload.pageSize,
        totalCount: action.payload.totalCount,
        totalPages: Math.ceil(
          action.payload.totalCount / action.payload.pageSize
        ),
      };
      state.lastUpdated = Date.now();
    });
    builder.addCase(fetchPrintersPaged.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // fetchPrinterById
    builder.addCase(fetchPrinterById.pending, state => {
      state.loadingCurrent = true;
      state.currentError = null;
    });
    builder.addCase(fetchPrinterById.fulfilled, (state, action) => {
      state.loadingCurrent = false;
      state.currentPrinter = action.payload;
    });
    builder.addCase(fetchPrinterById.rejected, (state, action) => {
      state.loadingCurrent = false;
      state.currentError = action.payload as string;
    });

    // createPrinter
    builder.addCase(createPrinter.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createPrinter.fulfilled, (state, action) => {
      state.loading = false;
      state.printers.push(action.payload);

      // Agregar también al terminal correspondiente
      const terminalId = action.payload.poS_TerminalId;
      if (!state.printersByTerminal[terminalId]) {
        state.printersByTerminal[terminalId] = [];
      }
      state.printersByTerminal[terminalId].push(action.payload);
    });
    builder.addCase(createPrinter.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // updatePrinter
    builder.addCase(updatePrinter.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updatePrinter.fulfilled, (state, action) => {
      state.loading = false;

      // Actualizar en la lista principal
      const index = state.printers.findIndex(
        p => p.poS_PrinterId === action.payload.poS_PrinterId
      );
      if (index !== -1) {
        state.printers[index] = action.payload;
      }

      // Actualizar en printersByTerminal
      const terminalId = action.payload.poS_TerminalId;
      if (state.printersByTerminal[terminalId]) {
        const terminalIndex = state.printersByTerminal[terminalId].findIndex(
          p => p.poS_PrinterId === action.payload.poS_PrinterId
        );
        if (terminalIndex !== -1) {
          state.printersByTerminal[terminalId][terminalIndex] = action.payload;
        }
      }

      // Actualizar currentPrinter si es el mismo
      if (
        state.currentPrinter?.poS_PrinterId === action.payload.poS_PrinterId
      ) {
        state.currentPrinter = action.payload;
      }

      // Actualizar defaultPrinter si es el mismo
      if (
        state.defaultPrinter?.poS_PrinterId === action.payload.poS_PrinterId
      ) {
        state.defaultPrinter = action.payload;
      }
    });
    builder.addCase(updatePrinter.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // deletePrinter
    builder.addCase(deletePrinter.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deletePrinter.fulfilled, (state, action) => {
      state.loading = false;
      const printerId = action.payload;

      // Remover de la lista principal
      state.printers = state.printers.filter(
        p => p.poS_PrinterId !== printerId
      );

      // Remover de todos los terminales
      Object.keys(state.printersByTerminal).forEach(key => {
        const terminalId = parseInt(key);
        state.printersByTerminal[terminalId] = state.printersByTerminal[
          terminalId
        ].filter(p => p.poS_PrinterId !== printerId);
      });

      // Limpiar currentPrinter si es el mismo
      if (state.currentPrinter?.poS_PrinterId === printerId) {
        state.currentPrinter = null;
      }

      // Limpiar defaultPrinter si es el mismo
      if (state.defaultPrinter?.poS_PrinterId === printerId) {
        state.defaultPrinter = null;
      }
    });
    builder.addCase(deletePrinter.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // setDefaultPrinter
    builder.addCase(setDefaultPrinter.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(setDefaultPrinter.fulfilled, (state, action) => {
      state.loading = false;
      state.defaultPrinter = action.payload;

      // Actualizar todas las impresoras para reflejar el cambio de IsDefault
      state.printers = state.printers.map(printer => ({
        ...printer,
        isDefault: printer.poS_PrinterId === action.payload.poS_PrinterId,
      }));

      // Actualizar en printersByTerminal
      Object.keys(state.printersByTerminal).forEach(key => {
        const terminalId = parseInt(key);
        state.printersByTerminal[terminalId] = state.printersByTerminal[
          terminalId
        ].map(printer => ({
          ...printer,
          isDefault: printer.poS_PrinterId === action.payload.poS_PrinterId,
        }));
      });
    });
    builder.addCase(setDefaultPrinter.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // fetchDefaultPrinter
    builder.addCase(fetchDefaultPrinter.pending, state => {
      state.loadingDefault = true;
      state.defaultError = null;
    });
    builder.addCase(fetchDefaultPrinter.fulfilled, (state, action) => {
      state.loadingDefault = false;
      state.defaultPrinter = action.payload;
    });
    builder.addCase(fetchDefaultPrinter.rejected, (state, action) => {
      state.loadingDefault = false;
      state.defaultError = action.payload as string;
    });

    // fetchPrintersByTerminal
    builder.addCase(fetchPrintersByTerminal.pending, (state, action) => {
      const terminalId = action.meta.arg;
      state.loadingTerminal[terminalId] = true;
      delete state.terminalErrors[terminalId];
    });
    builder.addCase(fetchPrintersByTerminal.fulfilled, (state, action) => {
      const { terminalId, printers } = action.payload;
      state.loadingTerminal[terminalId] = false;
      state.printersByTerminal[terminalId] = printers;
    });
    builder.addCase(fetchPrintersByTerminal.rejected, (state, action) => {
      const payload = action.payload as { terminalId: number; error: string };
      state.loadingTerminal[payload.terminalId] = false;
      state.terminalErrors[payload.terminalId] = payload.error;
    });

    // activatePrinter
    builder.addCase(activatePrinter.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(activatePrinter.fulfilled, (state, action) => {
      state.loading = false;

      // Actualizar en la lista principal
      const index = state.printers.findIndex(
        p => p.poS_PrinterId === action.payload.poS_PrinterId
      );
      if (index !== -1) {
        state.printers[index] = action.payload;
      }

      // Actualizar en printersByTerminal
      const terminalId = action.payload.poS_TerminalId;
      if (state.printersByTerminal[terminalId]) {
        const terminalIndex = state.printersByTerminal[terminalId].findIndex(
          p => p.poS_PrinterId === action.payload.poS_PrinterId
        );
        if (terminalIndex !== -1) {
          state.printersByTerminal[terminalId][terminalIndex] = action.payload;
        }
      }
    });
    builder.addCase(activatePrinter.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

// Exportar acciones y reducer
export const {
  clearCurrentPrinter,
  clearError,
  setFilters,
  clearFilters,
  updatePrinterInList,
  removePrinterFromList,
  clearCache,
  resetPrinterState,
} = posPrinterSlice.actions;

export default posPrinterSlice.reducer;

// Selectors
export const selectPrinters = (state: { posPrinter: POSPrinterState }) =>
  state.posPrinter.printers;
export const selectCurrentPrinter = (state: { posPrinter: POSPrinterState }) =>
  state.posPrinter.currentPrinter;
export const selectDefaultPrinter = (state: { posPrinter: POSPrinterState }) =>
  state.posPrinter.defaultPrinter;
export const selectPrintersByTerminal =
  (terminalId: number) => (state: { posPrinter: POSPrinterState }) =>
    state.posPrinter.printersByTerminal[terminalId] || [];
export const selectPrintersLoading = (state: { posPrinter: POSPrinterState }) =>
  state.posPrinter.loading;
export const selectCurrentPrinterLoading = (state: {
  posPrinter: POSPrinterState;
}) => state.posPrinter.loadingCurrent;
export const selectDefaultPrinterLoading = (state: {
  posPrinter: POSPrinterState;
}) => state.posPrinter.loadingDefault;
export const selectTerminalPrintersLoading =
  (terminalId: number) => (state: { posPrinter: POSPrinterState }) =>
    state.posPrinter.loadingTerminal[terminalId] || false;
export const selectPrintersError = (state: { posPrinter: POSPrinterState }) =>
  state.posPrinter.error;
export const selectCurrentPrinterError = (state: {
  posPrinter: POSPrinterState;
}) => state.posPrinter.currentError;
export const selectDefaultPrinterError = (state: {
  posPrinter: POSPrinterState;
}) => state.posPrinter.defaultError;
export const selectTerminalPrintersError =
  (terminalId: number) => (state: { posPrinter: POSPrinterState }) =>
    state.posPrinter.terminalErrors[terminalId];
export const selectPagination = (state: { posPrinter: POSPrinterState }) =>
  state.posPrinter.pagination;
export const selectCurrentFilters = (state: { posPrinter: POSPrinterState }) =>
  state.posPrinter.currentFilters;
export const selectLastUpdated = (state: { posPrinter: POSPrinterState }) =>
  state.posPrinter.lastUpdated;

// Selectores combinados
export const selectActivePrinters = (state: { posPrinter: POSPrinterState }) =>
  state.posPrinter.printers.filter(printer => printer.status);

export const selectInactivePrinters = (state: {
  posPrinter: POSPrinterState;
}) => state.posPrinter.printers.filter(printer => !printer.status);

export const selectDefaultPrinterId = (state: {
  posPrinter: POSPrinterState;
}) => state.posPrinter.defaultPrinter?.poS_PrinterId;

export const selectPrinterById =
  (printerId: string) => (state: { posPrinter: POSPrinterState }) =>
    state.posPrinter.printers.find(p => p.poS_PrinterId === printerId);

// Helper para obtener selectores con memoización (si usas reselect)
import { createSelector } from "reselect";

export const selectPrintersMemoized = createSelector(
  [selectPrinters],
  printers => printers
);

export const selectActivePrintersMemoized = createSelector(
  [selectPrinters],
  printers => printers.filter(printer => printer.status)
);
